import { AppNavigationProvider } from '@euler/app/components/AppNavigationProvider';
import { BackIcon } from '@euler/app/components/icons/BackIcon';
import { QrcodeSharingScreen } from '@euler/app/flows/common/QrcodeSharing';
import { VehicleSubscriptionsScreen } from '@euler/app/flows/common/VehicleSubscriptionsScreen';
import { ProductFlow } from '@euler/app/flows/products/ProductFlow';
import { ServiceRecordMediasPage } from '@euler/app/flows/record/pages/medias/ServiceRecordMediasPage';
import { ServiceRecordsPage } from '@euler/app/flows/record/pages/records/ServiceRecordsPage';
import { ReportScreen } from '@euler/app/flows/report/pages/Report';
import { ReportScreens } from '@euler/app/flows/report/ReportScreens';
import { TaskManager } from '@euler/app/flows/task/functions/task-detail/TaskManager';
import {
  getTaskResourceNamespace,
  TaskContextProvider,
} from '@euler/app/flows/task/functions/TaskContext';
import { ConstructionJobScreen } from '@euler/app/flows/task/pages/construction/ConstructionJobScreen';
import { ProductAdoptionConfirmationPage } from '@euler/app/flows/task/pages/construction/ProductAdoptionConfirmationPage';
import { ScheduleConstructionJobFlow } from '@euler/app/flows/task/pages/construction/ScheduleConstructionJobFlow';
import { SelectCloserPage } from '@euler/app/flows/task/pages/construction/SelectCloserPage';
import { DeliveryCheckScreen } from '@euler/app/flows/task/pages/delivery-check/DeliveryCheckScreen';
import { ReworkRecordFlow } from '@euler/app/flows/task/pages/delivery-check/rework-record/ReworkRecordFlow';
import { ReworkRecordsPage } from '@euler/app/flows/task/pages/delivery-check/ReworkRecordsPage';
import { SamplingCheckScreen } from '@euler/app/flows/task/pages/delivery-check/SamplingCheckScreen';
import { InspectionFlowsScreen } from '@euler/app/flows/task/pages/detail/InspectionFlowsScreen';
import { InspectionTemplatePreviewFlow } from '@euler/app/flows/task/pages/detail/InspectionTemplatePreviewFlow';
import { TaskDetailScreen } from '@euler/app/flows/task/pages/detail/TaskDetailScreen';
import { HoorayScreen } from '@euler/app/flows/task/pages/Hooray';
import { InspectionScreen } from '@euler/app/flows/task/pages/inspection/InspectionScreen';
import { InspectionSiteSearchScreen } from '@euler/app/flows/task/pages/inspection/InspectionSiteSearchScreen';
import { PendingIssuesPage } from '@euler/app/flows/task/pages/PendingIssuesPage';
import { PendingIssueWithDetailsPage } from '@euler/app/flows/task/pages/PendingIssueWithDetailsPage';
import { PreInspectionScreen } from '@euler/app/flows/task/pages/pre-inspection/PreInspectionScreen';
import { QuotationFlow } from '@euler/app/flows/task/pages/quotation/QuotationFlow';
import { TaskMediaAlbumsPage } from '@euler/app/flows/task/pages/TaskMediaAlbumsPage';
import { TaskOrderDetailPage } from '@euler/app/flows/task/pages/TaskOrderDetailPage';
import { TaskTimeoutInfoPage } from '@euler/app/flows/task/pages/TaskTimeoutInfoPage';
import { TaskTimelineScreen } from '@euler/app/flows/task/pages/Timeline';
import { ErrorFallback } from '@euler/components/error';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { MultilineHeaderTitle } from '@euler/components/MultilineHeaderTitle';
import { FontFamily } from '@euler/components/typography';
import { useRegisterActiveEntity } from '@euler/functions/ActiveEntityContext';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ScreenToParams, spreadScreens } from '@euler/functions/spreadScreens';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import {
  ScopedObservableSuspenseResourceContextProvider,
  usePersistFn,
} from '@euler/utils/hooks';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import { useNavigation } from '@react-navigation/native';
import {
  CardStyleInterpolators,
  createStackNavigator,
  TransitionPresets,
} from '@react-navigation/stack';
import * as Sentry from '@sentry/react';
import { StatusBar } from 'expo-status-bar';
import { memo, useEffect, useMemo } from 'react';
import { Slot } from 'react-slot-fill';
import { useTheme } from 'styled-components';
import { FinishTaskPage } from './pages/finish-task';

const Stack = createStackNavigator();

export const TaskScreens = {
  taskDetail: TaskDetailScreen,
  timeline: TaskTimelineScreen,
  qrcodeSharing: QrcodeSharingScreen,
  inspectionFlows: InspectionFlowsScreen,
  inspection: InspectionScreen,
  inspectionTemplatePreview: InspectionTemplatePreviewFlow,
  preInspection: PreInspectionScreen,
  records: ServiceRecordsPage,

  _vehicleSubscriptions: VehicleSubscriptionsScreen,
  _pendingIssues: PendingIssuesPage,
  _pendingIssueWithDetails: PendingIssueWithDetailsPage,
  _scheduleConstructionJobs: ScheduleConstructionJobFlow,
  _constructionJob: ConstructionJobScreen,
  _finish: FinishTaskPage,
  _timeoutInfo: TaskTimeoutInfoPage,

  _inspectionSiteSearch: InspectionSiteSearchScreen,
  _deliveryCheck: DeliveryCheckScreen,
  _samplingCheck: SamplingCheckScreen,
  _reworkRecord: ReworkRecordFlow,
  _reworkRecords: ReworkRecordsPage,
  _hooray: HoorayScreen,
  _orderDetail: TaskOrderDetailPage,
  _mediaAlbums: TaskMediaAlbumsPage,
  _medias: ServiceRecordMediasPage,

  // product related pages
  _products: ProductFlow,
  _confirmProductAddition: ProductAdoptionConfirmationPage,
  _selectCloser: SelectCloserPage,

  // quotation related pages
  _quotation: QuotationFlow,

  // report related pages
  report: ReportScreen,
  ...ReportScreens,
};

export type TaskScreenType = keyof typeof TaskScreens;

export type TaskNavParams = ScreenToParams<typeof TaskScreens>;

type Props = {
  taskNo: string;
  continueTo?: TaskScreenType;
  from?: string;
  continueParams?: Record<string, any>;
  presentedAs?: 'replace';
  onEnd?: () => void;
};

export const TaskFlow = wrapNavigatorScreen(
  ({ onEnd, ...props }: Props) => {
    useMemo(() => {
      TaskManager.tryRecover(props.taskNo);
    }, [props.taskNo]);

    // Publish the focused task so out-of-flow surfaces (the floating chat
    // panel) can tell when their order is already on screen.
    useRegisterActiveEntity({ kind: 'task', taskNo: props.taskNo });

    const nav = useNavigation();

    const callbackEnd = usePersistFn(() => {
      onEnd?.();
    });

    useEffect(() => {
      return nav.addListener('beforeRemove', () => {
        setTimeout(callbackEnd, 500);
      });
    }, [callbackEnd, nav]);

    return <Content {...props} />;
  },
  ({ route }) => ({
    headerShown: false,
    ...(route.params.presentedAs === 'replace'
      ? {
          cardStyleInterpolator: CardStyleInterpolators.forFadeFromCenter,
        }
      : {}),
  }),
);

const Content = memo((props: Props) => {
  const theme = useTheme();
  const { rpx } = useResponsiveContext();
  const resourceNamespace = getTaskResourceNamespace(props.taskNo);
  return (
    <AppNavigationProvider>
      <LayoutProviderView fallbackToChildren style={{ flex: 1 }}>
        <Sentry.ErrorBoundary
          // @ts-ignore
          fallback={(params: any) => <ErrorFallback {...params} />}
        >
          <ScopedObservableSuspenseResourceContextProvider
            scope={resourceNamespace}
          >
            <TaskContextProvider
              taskNo={props.taskNo}
              continueTo={props.continueTo}
              from={props.from}
              continueParams={props.continueParams}
            >
              <BottomSheetModalProvider>
                <StatusBar style={'dark'} />
                <Stack.Navigator
                  initialRouteName={'taskDetail'}
                  screenOptions={{
                    ...TransitionPresets.SlideFromRightIOS,
                    headerMode: 'float',
                    headerTitleAlign: 'center',
                    headerBackTitle: ' ',
                    headerTitleStyle: {
                      fontSize: rpx(18),
                      fontFamily: FontFamily.NotoSans.Bold,
                    },
                    cardStyle: {
                      backgroundColor: theme.page.card.backgroundColor,
                    },
                    headerTitle: MultilineHeaderTitle,
                    headerBackImage: () => <BackIcon />,
                  }}
                >
                  {spreadScreens(
                    Stack.Screen,
                    TaskScreens,
                    'taskDetail',
                    props,
                  )}
                </Stack.Navigator>
                <Slot name="task:bottom-panel" />
              </BottomSheetModalProvider>
            </TaskContextProvider>
          </ScopedObservableSuspenseResourceContextProvider>
        </Sentry.ErrorBoundary>
      </LayoutProviderView>
    </AppNavigationProvider>
  );
});
